import type { FC } from 'react';
import { Fragment } from 'react';
import { Divider, Tooltip, Typography } from '@mui/material';
import type { CreativeEntry } from 'types/shared';
import {
  CardHeader,
  CardHeaderMain,
  CreativeRowLayout,
  CreativeRowMain,
  EmptyStateText,
  PlaceholderIcon,
  PreviewCard,
  ThumbnailImage,
  ThumbnailPlaceholder,
  TooltipImage,
  TypeLabel,
  ValueColumn,
} from './LaunchPreview.styles';

interface CreativeThumbnailProps {
  previewLink: string | null;
  name: string;
}

const CreativeThumbnail: FC<CreativeThumbnailProps> = ({ previewLink, name }) => {
  if (!previewLink) {
    return (
      <ThumbnailPlaceholder>
        <PlaceholderIcon />
      </ThumbnailPlaceholder>
    );
  }

  return (
    <Tooltip title={<TooltipImage src={previewLink} alt={name} />} placement="right" arrow>
      <ThumbnailImage src={previewLink} alt={name} loading="lazy" decoding="async" />
    </Tooltip>
  );
};

interface CreativeRowProps {
  creative: CreativeEntry | undefined;
  fallbackId: string | undefined;
}

const CreativeRow: FC<CreativeRowProps> = ({ creative, fallbackId }) => (
  <CreativeRowLayout>
    <CreativeRowMain>
      <CreativeThumbnail
        previewLink={creative?.thumbnail_url ?? creative?.url ?? null}
        name={creative?.name ?? fallbackId ?? ''}
      />
      <Typography variant="body2" noWrap>
        {creative?.name ?? fallbackId ?? 'Unknown creative'}
      </Typography>
    </CreativeRowMain>
    {/* TPC date isn't available on the plain creatives endpoint yet (only
      Hypothesis Testing creatives carry tpc_finish_date) — shown as a dash
      until the backend exposes it here too. */}
    <ValueColumn variant="body2" color="text.secondary">
      -
    </ValueColumn>
  </CreativeRowLayout>
);

interface CreativeListCardProps {
  headerLabel: string;
  countLabel: string;
  typeLabel?: string;
  creativeIds: (string | undefined)[];
  lookup: Map<string, CreativeEntry>;
}

const CreativeListCard: FC<CreativeListCardProps> = ({
  headerLabel,
  countLabel,
  typeLabel,
  creativeIds,
  lookup,
}) => (
  <PreviewCard>
    <CardHeader>
      <CardHeaderMain>
        <Typography variant="subtitle2" fontWeight={600}>
          {headerLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {countLabel}
        </Typography>
        {typeLabel ? (
          <TypeLabel variant="caption">{typeLabel}</TypeLabel>
        ) : null}
      </CardHeaderMain>
      <ValueColumn variant="caption" color="text.secondary">
        TPC date
      </ValueColumn>
    </CardHeader>
    <Divider />
    {creativeIds.length ? (
      creativeIds.map((id, index) => (
        <Fragment key={id ?? `creative-${String(index)}`}>
          {index > 0 && <Divider />}
          <CreativeRow creative={id ? lookup.get(id) : undefined} fallbackId={id} />
        </Fragment>
      ))
    ) : (
      <EmptyStateText variant="body2">No creatives selected</EmptyStateText>
    )}
  </PreviewCard>
);

export default CreativeListCard;
